import {
  Flex, Text, Skeleton,
  useColorModeValue,
} from "@chakra-ui/react";
import { isImmediate } from "features/Resources/Shared/helpers";
import { useSharedResourcesListByNewHireIdQuery } from "features/Resources/Shared/hooks";
import { NewHireJourney } from "models/v2/newHireJourney";
import { PropsWithChildren, useMemo } from "react";

interface ActionOverviewProps extends PropsWithChildren {
  newHireJourney: NewHireJourney;
}

export function ImmediateActionsSummary({ newHireJourney }: ActionOverviewProps) {
  const highlightColor = useColorModeValue("brand.500", "brand.100");
  const { data } = useSharedResourcesListByNewHireIdQuery(newHireJourney?.id);
  const immediateActionsLength = useMemo(
    () => (data || [])
      .filter((v) => v.action.status !== "skipped")
      .filter((action) => isImmediate(action))
      .length,
    [data]
  );

  if (!newHireJourney) {
    return <Skeleton height="20px" />;
  }

  if (!immediateActionsLength) {
    return (
      <Text fontSize="xl" color="fg.muted">
        This new hire does not have any emails, chats, or tasks that will be immediately sent.
      </Text>
    );
  }

  const TotalText = (
    <Text as="span" fontWeight="bold" color={highlightColor}>
      {`${immediateActionsLength} actions`}
    </Text>
  );

  return (
    <Flex direction="column" gap="4">
      <Text fontSize="xl" color="fg.muted">
        {`When you kickoff this onboarding, Camino will send `}
        {TotalText}
        {` immediately.`}
      </Text>
    </Flex>
  );
}

export function TotalActionsSummary({ newHireJourney }: ActionOverviewProps) {
  const highlightColor = useColorModeValue("brand.500", "brand.100");
  const { data: actions, isFetching } = useSharedResourcesListByNewHireIdQuery(newHireJourney?.id);
  const totalActionsLength = useMemo(
    () => (actions || [])
      .filter((v) => v.action.status !== "skipped")
      .length,
    [actions]
  );

  if (!newHireJourney || isFetching) {
    return <Skeleton height="20px" />;
  }

  if (!totalActionsLength) {
    return (
      <Text fontSize="xl" color="fg.muted">
        This new hire does not have any emails, chats, or tasks for their onboarding.
      </Text>
    );
  }

  const TotalText = (
    <Text as="span" fontWeight="bold" color={highlightColor}>
      {`${totalActionsLength} total actions `}
    </Text>
  );

  return (
    <Flex direction="column" gap="4">
      <Text fontSize="xl" color="fg.muted">
        {TotalText}
        will be sent during onboarding.
      </Text>
    </Flex>
  );
}
